import type { IssueTypeStatus, Stored } from "@overmap-ai/common"
import type { ReactNode } from "react"

import {
	IssueTypeStatusEqualsFilterModifier,
	IssueTypeStatusExcludesFilterModifier,
	IssueTypeStatusIncludesFilterModifier,
	IssueTypeStatusNotEqualsFilterModifier,
} from "../../modifiers"
import { BaseFilterDef, createFilterModifierConfig, type FilterDefRenderProps } from "../BaseFilterDef"
import { IssueTypeStatusFilterCell } from "./IssueTypeStatusFilterCell"

type IssueTypeStatusFieldValue = IssueTypeStatus["uuid"]
type IssueTypeStatusFilterValue = IssueTypeStatus["uuid"][] | IssueTypeStatus["uuid"]

export class IssueTypeStatusFilterDef extends BaseFilterDef<IssueTypeStatusFieldValue, IssueTypeStatusFilterValue> {
	readonly id = "issue-status"
	readonly defaultFilterValue: IssueTypeStatusFilterValue | undefined = undefined
	readonly defaultFilterModifier: keyof typeof this.modifiers = "equals"

	readonly issueTypeStatuses: Stored<IssueTypeStatus>[]

	constructor(issueTypeStatuses: Stored<IssueTypeStatus>[]) {
		super()
		this.issueTypeStatuses = issueTypeStatuses
	}

	readonly modifiers = {
		equals: createFilterModifierConfig({
			modifier: IssueTypeStatusEqualsFilterModifier,
			isValueValid: (_value: IssueTypeStatusFieldValue): _value is IssueTypeStatus["uuid"] => true,
			isFilterValueValid: (filterValue: IssueTypeStatusFilterValue): filterValue is IssueTypeStatus["uuid"] =>
				!Array.isArray(filterValue),
		}),
		notEquals: createFilterModifierConfig({
			modifier: IssueTypeStatusNotEqualsFilterModifier,
			isValueValid: (_value: IssueTypeStatusFieldValue): _value is IssueTypeStatus["uuid"] => true,
			isFilterValueValid: (filterValue: IssueTypeStatusFilterValue): filterValue is IssueTypeStatus["uuid"] =>
				!Array.isArray(filterValue),
		}),
		includes: createFilterModifierConfig({
			modifier: IssueTypeStatusIncludesFilterModifier,
			isValueValid: (_value: IssueTypeStatusFieldValue): _value is IssueTypeStatus["uuid"] => true,
			isFilterValueValid: (filterValue: IssueTypeStatusFilterValue): filterValue is IssueTypeStatus["uuid"][] =>
				Array.isArray(filterValue),
		}),
		excludes: createFilterModifierConfig({
			modifier: IssueTypeStatusExcludesFilterModifier,
			isValueValid: (_value: IssueTypeStatusFieldValue): _value is IssueTypeStatus["uuid"] => true,
			isFilterValueValid: (filterValue: IssueTypeStatusFilterValue): filterValue is IssueTypeStatus["uuid"][] =>
				Array.isArray(filterValue),
		}),
	}

	render(props: FilterDefRenderProps<IssueTypeStatusFilterDef>): ReactNode {
		return <IssueTypeStatusFilterCell key={props.filter.id} {...props} />
	}
}
